<template>
  <v-card class="rounded-lg" outlined>
    <v-card-title>
      <v-icon left>mdi-chart-box</v-icon>
      EOD Summary
    </v-card-title>
    <v-card-text>
      <!-- Sub-reconciliation status -->
      <div class="mb-4">
        <div class="text-subtitle-2 mb-2">ສະຖານະການກວດສອບ</div>
        <v-row dense>
          <v-col
            v-for="sub in subReconciliations"
            :key="sub.id"
            cols="12"
            sm="4"
          >
            <v-chip
              :color="sub.status === 'completed' ? 'green' : (sub.status === 'in_progress' ? 'blue' : 'grey')"
              dark
              small
              class="mr-1"
            >
              <v-icon x-small left>{{ sub.status === 'completed' ? 'mdi-check' : 'mdi-clock' }}</v-icon>
              {{ sub.bank_account.label }}
            </v-chip>
          </v-col>
        </v-row>
      </div>

      <v-divider class="mb-4" />

      <!-- Summary totals -->
      <v-row v-if="summary" dense>
        <v-col cols="12" sm="4">
          <div class="text-caption grey--text">ຍອດ Statement ລວມ</div>
          <div class="text-h6 font-weight-bold">{{ formatCurrency(summary.statement_summary.total_statement_amount) }}</div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="text-caption grey--text">ຍອດກົງກັນ (QR + Statement)</div>
          <div class="text-h6 font-weight-bold green--text">{{ formatCurrency(summary.qr_summary.system_confirmed) }}</div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="text-caption grey--text">ລໍຖ້າກວດສອບ</div>
          <div class="text-h6 font-weight-bold" :class="totalPending > 0 ? 'orange--text' : 'green--text'">
            {{ totalPending }} ລາຍການ
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <!-- Exception transactions -->
      <div v-if="summary && summary.exception_transactions && summary.exception_transactions.length > 0">
        <div class="text-subtitle-2 mb-2 orange--text">
          <v-icon small color="orange">mdi-alert</v-icon>
          ລາຍການທີ່ຍັງບໍ່ກົງກັນ ({{ summary.exception_transactions.length }})
        </div>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>ແຫຼ່ງ</th>
              <th>ລາຍລະອຽດ</th>
              <th class="text-right">ຈຳນວນ</th>
              <th>ສະຖານະ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(exc, i) in summary.exception_transactions" :key="i">
              <td>
                <v-chip x-small :color="exc.source === 'qr_reconciliation' ? 'purple' : 'blue'" dark>
                  {{ exc.source === 'qr_reconciliation' ? 'QR' : exc.bank_account }}
                </v-chip>
              </td>
              <td class="text-caption">{{ exc.description || exc.bill_number || exc.transaction_ref }}</td>
              <td class="text-right">{{ formatCurrency(exc.amount) }}</td>
              <td>
                <v-chip x-small color="orange" dark>{{ exc.match_status }}</v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <!-- Actions -->
      <div class="mt-4 d-flex justify-end">
        <v-btn
          v-if="summary && summary.can_close"
          color="success"
          depressed
          @click="$emit('close-eod')"
        >
          <v-icon left>mdi-check-circle</v-icon>
          ປິດ EOD
        </v-btn>
        <v-btn
          v-else-if="summary && !summary.can_close"
          color="orange"
          dark
          depressed
          @click="showProposeDialog = true"
        >
          <v-icon left>mdi-alert-circle</v-icon>
          ສະເໜີປິດ (ມີຂໍ້ຍົກເວັ້ນ)
        </v-btn>
      </div>

      <!-- Propose close dialog -->
      <v-dialog v-model="showProposeDialog" max-width="600">
        <v-card>
          <v-card-title>ສະເໜີປິດ EOD ພ້ອມຂໍ້ຍົກເວັ້ນ</v-card-title>
          <v-card-text>
            <div class="mb-3 orange--text text-body-2">
              ມີ {{ summary ? summary.exception_transactions.length : 0 }} ລາຍການທີ່ຍັງບໍ່ກົງກັນ.
              ກະລຸນາໃຫ້ເຫດຜົນ.
            </div>
            <v-textarea
              v-model="proposeComment"
              label="ເຫດຜົນ"
              outlined
              rows="3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showProposeDialog = false">ຍົກເລີກ</v-btn>
            <v-btn
              color="orange"
              dark
              depressed
              :disabled="!proposeComment"
              :loading="proposing"
              @click="proposeClose"
            >
              ສະເໜີປິດ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'EodSummaryPanel',
  props: {
    batchId: { type: String, required: true },
    summary: { type: Object, default: null },
    subReconciliations: { type: Array, default: () => [] },
  },
  data() {
    return {
      showProposeDialog: false,
      proposeComment: '',
      proposing: false,
    };
  },
  computed: {
    totalPending() {
      if (!this.summary) return 0;
      return (this.summary.statement_summary?.total_pending_count || 0)
        + (this.summary.qr_summary?.pending_count || 0);
    },
  },
  methods: {
    async proposeClose() {
      this.proposing = true;
      try {
        await this.$axios.post(`ldb-reconciliation/${this.batchId}/propose-close`, {
          comment: this.proposeComment,
          exception_transactions: this.summary?.exception_transactions || [],
        });
        this.showProposeDialog = false;
        this.$emit('proposed');
        this.$store.commit('Toast_State', { value: true, color: 'success', msg: 'ສະເໜີປິດສຳເລັດ' });
      } catch (e) {
        const msg = e.response?.data?.message || 'ສະເໜີລົ້ມເຫຼວ';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.proposing = false;
      }
    },
    formatCurrency(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
  },
};
</script>
